import { format, parseISO } from 'date-fns';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TransactionsSectionList, {
  TransactionGroup,
} from 'shared/components/TransactionSectionList/TransactionSectionList';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { amountStyles } from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import useVlocHistory from './hooks/useVlocHistory';

const VlocHistory = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'history',
  });

  const { bottom } = useSafeAreaInsets();

  const { vlocData, loading = false, errorModel } = useVlocHistory(id);

  const handleTransactionDetails = (lineOfCreditId: string) => {
    router.navigate({
      pathname: '/varo-line-of-credit/history-details',
      params: { id: lineOfCreditId },
    });
  };

  const historySectionData: TransactionGroup[] =
    vlocData?.sections?.map((section) => {
      return {
        title: format(parseISO(section.date), 'MMMM yyyy'),
        transactions: section.items.map((item) => {
          return {
            titleText: translatedText('itemTitle'),
            amountText: `+ ${item?.disbursementAmount?.formattedWithPlusPrefix}`,
            isPending: false,
            secondaryText: format(
              parseISO(item?.originationDate),
              'MMMM dd, yyyy',
            ),
            amountStyle: amountStyles.POSITIVE,
            analyticsId: 'history-item',
            customAttributes: {
              account_type: 'vloc',
              amount: item?.disbursementAmount?.formattedWithPlusPrefix,
            },
            onPress: () => handleTransactionDetails(item?.lineOfCreditId),
          };
        }),
      };
    }) || ([] as TransactionGroup[]);

  const showError = errorModel && !loading;

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const goBack = () => {
    router.back();
  };

  useBackHandler(showError ? goHome : goBack);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('header'),
          headerBackVisible: !showError,
          headerShown: !showError,
          gestureEnabled: !showError,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      {historySectionData.length > 0 ? (
        <TransactionsSectionList transactions={historySectionData} />
      ) : (
        <EmptyStateScreen
          headlineText={translatedText('noHistoryTitle')}
          bodyText={translatedText('noHistorySubTitle')}
          illustrationKey="CASH_A"
        />
      )}

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          showError ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-vlocTransactionHistory"
              trackingAnalyticsId="vloc-history-error-tracking"
              trackingCustomAttributes={{
                step_name: VlocStepName.VLOC_TRANSACTION_HISTORY,
              }}
              insetBottomSafeArea={false}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingTop: theme.spacing.m,
  },
});

export default VlocHistory;
